/**
 * 表格筛选中的空值占位符，避免与真实值冲突。
 */
export const EMPTY_FILTER_VALUE = '__EMPTY__';

/**
 * 英语作文状态文案映射，供表格筛选和排序统一复用。
 */
export const ESSAY_STATUS_LABELS: Record<string, string> = {
  pending: '待上传',
  uploaded: '已上传',
  processing: '处理中',
  recognizing: '识别中',
  recognized: '已识别',
  correcting: '批改中',
  completed: '已完成',
  failed: '失败',
};

/**
 * 作文状态排序权重，数值越小越靠前。
 */
export const ESSAY_STATUS_ORDER: Record<string, number> = {
  pending: 1,
  uploaded: 2,
  processing: 3,
  recognizing: 4,
  recognized: 5,
  correcting: 6,
  completed: 7,
  failed: 8,
};

interface BuildFiltersOptions<T> {
  includeEmpty?: boolean;
  emptyText?: string;
  labelGetter?: (value: string, record: T) => string;
}

/**
 * 统一把任意值转成可比较的文本。
 *
 * @param value 原始值
 * @returns 去首尾空格后的字符串
 */
function toTextValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value).trim();
}

/**
 * 把原始值转成表格筛选器可用的稳定值。
 *
 * @param value 原始值
 * @returns 真实值或空值占位符
 */
function toFilterValue(value: unknown): string {
  const textValue = toTextValue(value);
  return textValue === '' ? EMPTY_FILTER_VALUE : textValue;
}

/**
 * 文本排序比较器。
 *
 * @param getter 从行数据中提取要比较的字段
 * @returns antd sorter 所需的比较函数
 */
export function compareText<T>(getter: (record: T) => unknown) {
  return (a: T, b: T) => (
    toTextValue(getter(a)).localeCompare(
      toTextValue(getter(b)),
      'zh-CN',
      {
        numeric: true,
        sensitivity: 'base',
      }
    )
  );
}

/**
 * 数值排序比较器。
 *
 * @param getter 从行数据中提取要比较的字段
 * @returns antd sorter 所需的比较函数
 */
export function compareNumber<T>(getter: (record: T) => unknown) {
  return (a: T, b: T) => {
    const left = Number(getter(a) ?? 0);
    const right = Number(getter(b) ?? 0);

    if (Number.isNaN(left) && Number.isNaN(right)) {
      return 0;
    }
    if (Number.isNaN(left)) {
      return -1;
    }
    if (Number.isNaN(right)) {
      return 1;
    }

    return left - right;
  };
}

/**
 * 日期排序比较器。
 *
 * @param getter 从行数据中提取日期字段
 * @returns antd sorter 所需的比较函数
 */
export function compareDate<T>(getter: (record: T) => unknown) {
  return (a: T, b: T) => {
    const left = Date.parse(toTextValue(getter(a)));
    const right = Date.parse(toTextValue(getter(b)));

    if (Number.isNaN(left) && Number.isNaN(right)) {
      return 0;
    }
    if (Number.isNaN(left)) {
      return -1;
    }
    if (Number.isNaN(right)) {
      return 1;
    }

    return left - right;
  };
}

/**
 * 基于当前表格数据生成去重后的筛选项。
 *
 * @param records 当前表格行数据
 * @param getter 从行数据中提取筛选字段
 * @param options 筛选项生成选项
 * @returns antd filters 结构
 */
export function buildFilters<T>(
  records: T[],
  getter: (record: T) => unknown,
  options: BuildFiltersOptions<T> = {}
) {
  const {
    includeEmpty = false,
    emptyText = '未填写',
    labelGetter,
  } = options;
  const filterMap = new Map<string, string>();

  records.forEach((record) => {
    const textValue = toTextValue(getter(record));
    const filterValue = toFilterValue(textValue);

    if (filterValue === EMPTY_FILTER_VALUE && !includeEmpty) {
      return;
    }

    if (!filterMap.has(filterValue)) {
      filterMap.set(
        filterValue,
        filterValue === EMPTY_FILTER_VALUE
          ? emptyText
          : (labelGetter ? labelGetter(textValue, record) : textValue)
      );
    }
  });

  return Array.from(filterMap.entries())
    .map(([value, text]) => ({ text, value }))
    .sort((a, b) => String(a.text).localeCompare(String(b.text), 'zh-CN', {
      numeric: true,
      sensitivity: 'base',
    }));
}

/**
 * 生成与 buildFilters 配套的筛选判断函数。
 *
 * @param getter 从行数据中提取筛选字段
 * @returns antd onFilter 所需的比较函数
 */
export function createValueFilter<T>(getter: (record: T) => unknown) {
  return (value: boolean | string | number | bigint, record: T) => (
    toFilterValue(getter(record)) === String(value)
  );
}

/**
 * 统一的作文状态排序比较器。
 *
 * @param getter 从行数据中提取状态字段
 * @returns antd sorter 所需的比较函数
 */
export function compareEssayStatus<T>(getter: (record: T) => string | undefined) {
  return (a: T, b: T) => (
    (ESSAY_STATUS_ORDER[getter(a) || 'pending'] || 999) -
    (ESSAY_STATUS_ORDER[getter(b) || 'pending'] || 999)
  );
}

/**
 * 统一获取作文状态文案。
 *
 * @param status 原始状态值
 * @returns 展示用中文文案
 */
export function getEssayStatusLabel(status?: string) {
  if (!status) {
    return '未知状态';
  }

  return ESSAY_STATUS_LABELS[status] || status;
}

/**
 * 根据分数映射到筛选分组，便于表格快速过滤。
 *
 * @param score 原始分数
 * @returns 分数分组标识
 */
export function getScoreBand(score?: number | null) {
  if (score === null || score === undefined || Number.isNaN(score) || score <= 0) {
    return 'pending';
  }
  if (score >= 20) {
    return 'excellent';
  }
  if (score >= 15) {
    return 'good';
  }
  return 'needs_improvement';
}

/**
 * 分数分组的固定筛选项。
 */
export const SCORE_BAND_FILTERS = [
  { text: '优秀 (>= 20)', value: 'excellent' },
  { text: '良好 (15-19.9)', value: 'good' },
  { text: '待提升 (< 15)', value: 'needs_improvement' },
  { text: '待批改', value: 'pending' },
];
